import {
  TContextEnvironment,
  TDataLayer,
} from "@porsche-customer/porschedatalayer-events/projects/navi/all/types";
import { DataLayerLibrary } from "@porsche-customer/porschedatalayer-library";
import { Environment, Environments } from "../../entities/environment";
import { splitLocale } from "../../entities/locale";
import {
  DATA_LAYER_APPLICATION_NAME,
  DATA_LAYER_APP_ID,
  EventAction,
} from "../../utility/constants/analytics";
import { isString } from "../../utility/helper";

function getEnvironmentAbbreviation(env: Environment): TContextEnvironment {
  switch (env) {
    case Environments.PRODUCTION:
      return "P";
    case Environments.PREVIEW:
      return "PP";
    default:
      return "K";
  }
}

export class GlobalDataLayerService {
  homeNavEventId = /^PAGHomeNav_/;

  gdl!: DataLayerLibrary<TDataLayer>;

  private static instance: GlobalDataLayerService;

  constructor() {
    this.initialise();
  }

  public static getInstance(): GlobalDataLayerService {
    if (!GlobalDataLayerService.instance) {
      GlobalDataLayerService.instance = new GlobalDataLayerService();
    }

    return GlobalDataLayerService.instance;
  }

  initialise(): void {
    this.gdl = new DataLayerLibrary();
  }

  pushDataLayerEvent(eventAction: EventAction, eventData?: TDataLayer): void {
    if (eventData) {
      this.gdl.push(eventAction, eventData);
    } else {
      this.gdl.push(eventAction);
    }
  }

  setEnvironment(env: Environment): void {
    this.gdl.setDefaultProperties(this.homeNavEventId, {
      context: { environment: getEnvironmentAbbreviation(env) },
    });
  }

  setPageName(pageName?: string): void {
    this.gdl.setDefaultProperties(this.homeNavEventId, {
      pageExperience: {
        pageName,
      },
    });
  }

  setUser(userId: string, ciamId: string, loginStatus: boolean): void {
    this.gdl.setDefaultProperties(this.homeNavEventId, {
      ids: { userId, ciamId },
      visitor: { loginStatus },
    });
  }

  setLocale(locale: string): void {
    try {
      const { country, language } = splitLocale(locale);

      this.gdl.setDefaultProperties(this.homeNavEventId, {
        context: { country, language },
      });
    } catch (err) {
      // throw the error away
    }
  }

  setDefaultProperties(locale: string, pageName?: string): void {
    let defaultGDLProperties: TDataLayer = {
      context: { applicationId: DATA_LAYER_APP_ID },
      visitor: { loginStatus: false },
    };

    try {
      const { country, language } = splitLocale(locale);

      defaultGDLProperties = {
        ...defaultGDLProperties,
        context: {
          ...defaultGDLProperties.context,
          country,
          language,
          applicationName: DATA_LAYER_APPLICATION_NAME,
        },
      };
    } catch (err) {
      // throw the error away
    }

    if (isString(pageName)) {
      defaultGDLProperties = {
        ...defaultGDLProperties,
        pageExperience: { pageName },
      };
    }

    this.gdl.setDefaultProperties(this.homeNavEventId, defaultGDLProperties);
  }
}
